import { Check, Monitor, Moon, Palette, Search, Sun } from 'lucide-react';
import * as React from 'react';

import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Input,
} from '@/components/cargofl-ui';
import {
  ACCENT_PRESETS,
  DEFAULT_ACCENT,
  DEFAULT_FONT_ID,
  FONT_OPTIONS,
  fontById,
  isFontAvailable,
  normalizeAccent,
  useAppearance,
  type Appearance,
  type FontOption,
} from '@/lib/appearance';
import { useTheme, type Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';

/**
 * The account menu's Appearance panel: theme, accent colour and UI font.
 *
 * Everything is a DropdownMenuItem with `onSelect` prevented rather than a
 * plain button - the item keeps arrow-key navigation, and preventing the select
 * keeps the panel open so a colour can be tried against the page behind it.
 */

const SECTION = 'px-1 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';
const RESET =
  'w-full justify-start px-1 py-1 text-xs text-muted-foreground focus:text-primary data-[disabled]:opacity-40';

const THEMES: ReadonlyArray<{ value: Theme; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Auto', icon: Monitor },
];

export function AppearanceMenu() {
  const { theme, setTheme } = useTheme();
  const { appearance, setAppearance } = useAppearance();

  const current = normalizeAccent(appearance.accent) || DEFAULT_ACCENT;
  const isDefaultAccent = !normalizeAccent(appearance.accent);
  const font = fontById(appearance.fontId);

  const setAccent = (hex: string | null) =>
    setAppearance({ ...appearance, accent: hex } satisfies Appearance);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        <Palette className="size-4" />
        Appearance
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-64 p-2">
        <p className={SECTION}>Theme</p>
        <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
          {THEMES.map(({ value, label, icon: Icon }) => (
            <DropdownMenuItem
              key={value}
              onSelect={(e) => e.preventDefault()}
              onClick={() => setTheme(value)}
              className={cn(
                'flex-col justify-center gap-0.5 rounded-[5px] px-1 py-1.5 text-[11px] font-medium',
                theme === value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground',
              )}
            >
              <Icon className="size-4" />
              {label}
            </DropdownMenuItem>
          ))}
        </div>

        <p className={cn(SECTION, 'pt-3')}>Accent color</p>
        <div className="flex flex-wrap items-center gap-2 px-1">
          {ACCENT_PRESETS.map((preset) => (
            <DropdownMenuItem
              key={preset.id}
              onSelect={(e) => e.preventDefault()}
              // Picking the product default stores null, not its hex: "never
              // chose" has to stay distinguishable from "deliberately picked
              // teal", so a future rebrand moves the first group and leaves the
              // second alone. Matched on the hex so reordering cannot break it.
              onClick={() => setAccent(preset.hex === DEFAULT_ACCENT ? null : preset.hex)}
              title={preset.label}
              aria-label={preset.label}
              className={cn(
                'size-6 justify-center rounded-full p-0 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-popover',
                current.toLowerCase() === preset.hex.toLowerCase() &&
                  'ring-2 ring-primary ring-offset-2 ring-offset-popover',
              )}
              style={{ backgroundColor: preset.hex }}
            />
          ))}
          {/* A native colour input rather than a hex box - it is the OS picker,
              it validates itself, and it costs no bundle. */}
          <DropdownMenuItem
            asChild
            onSelect={(e) => e.preventDefault()}
            className="size-6 justify-center rounded-full border border-dashed border-muted-foreground p-0"
          >
            <label title="Custom colour" style={{ backgroundColor: current }}>
              <input
                type="color"
                value={current}
                onChange={(e) => setAccent(e.target.value)}
                aria-label="Custom accent colour"
                className="size-0 opacity-0"
              />
            </label>
          </DropdownMenuItem>
        </div>
        <DropdownMenuItem
          onSelect={(e) => e.preventDefault()}
          onClick={() => setAccent(null)}
          disabled={isDefaultAccent}
          className={cn(RESET, 'mt-2')}
        >
          Reset to default
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="mt-1 border-t">
            Font
            <span
              className="ml-auto max-w-[110px] truncate pl-2 text-xs text-muted-foreground"
              style={{ fontFamily: font.stack }}
            >
              {font.label.replace(' (default)', '')}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56 p-2">
            <FontPicker
              selectedId={appearance.fontId || DEFAULT_FONT_ID}
              onSelect={(fontId) => setAppearance({ ...appearance, fontId })}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function FontPicker({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = React.useState('');

  // Probed once per mount, not per keystroke: `document.fonts.check` is cheap
  // but this runs over ~29 families, and what is installed cannot change while
  // the menu is open. A font the machine lacks is hidden outright - offering it
  // would silently render as Geist and read as a bug.
  const available = React.useMemo<FontOption[]>(() => FONT_OPTIONS.filter(isFontAvailable), []);
  const shown = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? available.filter((f) => f.label.toLowerCase().includes(q)) : available;
  }, [available, query]);

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Radix menus run typeahead on every character key, which would steal
          // each keystroke and jump focus to a matching row.
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Search fonts..."
          aria-label="Search fonts"
          className="h-8 pl-8 text-[13px]"
        />
      </div>

      <p className={cn(SECTION, 'pt-2.5')}>System</p>

      <div className="max-h-[248px] overflow-y-auto">
        {shown.map((f) => (
          <DropdownMenuItem
            key={f.id}
            onSelect={(e) => e.preventDefault()}
            onClick={() => onSelect(f.id)}
            className={cn('px-1.5', f.id === selectedId && 'font-medium')}
            // Rendered in its own face - the whole reason to list a font by name
            // is to see what you are choosing.
            style={{ fontFamily: f.stack }}
          >
            <span className="truncate">{f.label}</span>
            {f.id === selectedId ? <Check className="ml-auto size-4 text-primary" /> : null}
          </DropdownMenuItem>
        ))}
        {!shown.length && (
          <p className="px-1.5 py-3 text-center text-xs text-muted-foreground">No matching fonts</p>
        )}
      </div>

      <DropdownMenuItem
        onSelect={(e) => e.preventDefault()}
        onClick={() => onSelect(DEFAULT_FONT_ID)}
        disabled={selectedId === DEFAULT_FONT_ID}
        className={cn(RESET, 'mt-1 border-t pt-2')}
      >
        Reset to default
      </DropdownMenuItem>
    </div>
  );
}
